import { useState, type ReactNode } from "react";
import type { Plan } from "../../../shared/goals/plan";
import { Icon } from "../../components/ui/Icon";
import { Rich } from "../../components/ui/Rich";
import type { PlanComment } from "../../lib/goalsApi";
import { DocWalk } from "./DocWalk";
import { fmt } from "./text";
import { audienceSentence } from "../../../shared/goals/audience";

const WHY_TITLES = { places: "Why these places", ages: "Why these ages", starts: "Why these starting points", lists: "Why these lists", reach: "How far Meta may look" } as const;

/** The full plan as a walk, for when the owner wants the detail. Comments here go to "Ask for changes". */
export const PLAN_PARTS = [
  ["short", "In short"],
  ["goal", "The goal, as it understood it"],
  ["found", "What it found"],
  ["lessons", "What it starts from"],
  ["who", "Who"],
  ["angles", "Ad ideas"],
  ["tests", "Tests"],
  ["money", "Money"],
  ["time", "Timeline"],
  ["run", "How it runs"],
  ["risks", "Risks"],
  ["asks", "What it needs from you"],
  ["pbk", "How it follows the playbook"],
] as const;

const COLOURS = ["var(--fire)", "var(--fire-2)", "var(--violet)", "#81b29a", "var(--warn)", "#79828f", "#f08a7e", "#5d8bf4"];
export const MODE = { ask: "Asks you first", within_limits: "Within your limits", on_its_own: "On its own" } as const;
const USE = { retest: "Re-test here", fact: "Used as fact", watch: "Watch only", ctx: "Context" } as const;

const host = (u: string) => {
  const m = String(u).match(/https?:\/\/(?:www\.)?([^/\s]+)/g);
  return m ? [...new Set(m.map((x) => x.replace(/https?:\/\/(www\.)?/, "")))].slice(0, 2).join(" · ") : String(u).slice(0, 70);
};
const useOf = (how: string): keyof typeof USE => {
  const h = how.toLowerCase();
  return /re-?test|guess|confirm/.test(h) ? "retest" : /fact/.test(h) ? "fact" : /observ|watch/.test(h) ? "watch" : "ctx";
};

/** Evidence that names a lesson the plan uses leads with its id, as the prototype marks lessons. */
function Evidence({ text, ids }: { text: string; ids: string[] }) {
  const id = ids.find((x) => text.split(/[^A-Za-z0-9-]+/).includes(x));
  if (!id) return <>{text}</>;
  return (
    <>
      <span className="lid">{id}</span> {text.replace(id, "").replace(/^[\s:.,-]+/, "")}
    </>
  );
}

function K({ children, colour = "var(--dim)", margin = "16px 0 8px" }: { children: ReactNode; colour?: string; margin?: string }) {
  return <div style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: colour, margin }}>{children}</div>;
}

/** Each part of a plan, laid out the prototype's way; the full plan and the goal's plan page share them. */
export function planBodies(P: Plan): Record<string, ReactNode> {
  const angles = P.angles.toSorted((a, b) => a.rank - b.rank);
  const tot = P.budget.split.reduce((a, b) => a + b.usd, 0);
  return {
    short: (
      <>
        <Rich block className="dir rich pl-p" text={P.summary} />
        <div className="bet">
          <span className="kind" style={{ color: "var(--fire-2)" }}>
            <Icon name="target" />
            What has to go right
          </span>
          <p>{P.bet}</p>
        </div>
        {P.choices.length > 0 && (
          <>
            <K>What it decided</K>
            <ul className="bl">
              {P.choices.map((c, i) => (
                <li key={i}>
                  <b>{c.choice.replace(/\.$/, "")}.</b>
                  <Rich block className="rich pl-p pl-why" text={c.why} />
                </li>
              ))}
            </ul>
          </>
        )}
        <div className="why" style={{ marginTop: 12 }}>
          <b>Can it be done?</b>
          <Rich block className="rich pl-p" text={P.budget.reachable} />
        </div>
      </>
    ),
    goal: (
      <div className="rows">
        <div className="kv">
          <span>Goal</span>
          <span>{P.goal.statement}</span>
        </div>
        <div className="kv">
          <span>One result</span>
          <span>{P.goal.result}</span>
        </div>
        <div className="kv">
          <span>Pace</span>
          <span>{P.goal.pace_needed}</span>
        </div>
        <div className="kv">
          <span>Assumptions</span>
          <ul className="bl">
            {P.goal.assumptions.map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ul>
        </div>
      </div>
    ),
    found: (
      <div className="rows">
        {P.findings.map((f, i) => (
          <div className="fi" key={i}>
            <span className="n">{String(i + 1).padStart(2, "0")}</span>
            <div>
              {f.finding}
              <br />
              <span className="chip">{host(f.source)}</span>
            </div>
          </div>
        ))}
      </div>
    ),
    lessons: (
      <div className="rows">
        {P.lessons_used.map((u, i) => {
          const k = useOf(u.how);
          return (
            <div className="lu" key={i}>
              <span className="lid">{u.id}</span>
              <div>
                <p>{u.how}</p>
              </div>
              <span className={`use ${k}`}>{USE[k]}</span>
            </div>
          );
        })}
      </div>
    ),
    who: !P.audience ? null : "places" in P.audience ? (
      <div className="rows">
        <div className="kv">
          <span>Who</span>
          <span>{audienceSentence(P.audience).map((x) => x.text).join("")}</span>
        </div>
        <div className="kv">
          <span>How many</span>
          <span>{P.audience.size_basis}</span>
        </div>
        {Object.entries(P.audience.why).map(([part, why]) => (
          <div className="kv" key={part}>
            <span>{WHY_TITLES[part as keyof typeof WHY_TITLES]}</span>
            <span>{why}</span>
          </div>
        ))}
      </div>
    ) : (
      <div className="rows">
        <div className="kv">
          <span>Who</span>
          <span>{P.audience.who}</span>
        </div>
        <div className="kv">
          <span>How many</span>
          <span>
            {P.audience.size}
            <br />
            <span className="chip" style={{ marginTop: 6 }}>
              {P.audience.size_basis}
            </span>
          </span>
        </div>
        <div className="kv">
          <span>Where</span>
          <span>{P.audience.where}</span>
        </div>
      </div>
    ),
    angles: (
      <div className="angles">
        {angles.map((a, i) => (
          <div className={`ang${i === 0 ? " first" : ""}`} key={i}>
            <div>
              <span className="rank">{i === 0 ? "Goes first" : `Idea ${i + 1}`}</span>
              <h4>{a.name}</h4>
            </div>
            <p className="msg">{a.message}</p>
            <div>
              <div className="k">Why</div>
              <p style={{ fontSize: "var(--fs-small)" }}>{a.why}</p>
            </div>
            <div>
              <div className="k">Based on</div>
              <ul className="bl">
                {a.evidence.map((e, j) => (
                  <li key={j}>
                    <Evidence text={e} ids={P.lessons_used.map((l) => l.id)} />
                  </li>
                ))}
              </ul>
            </div>
            <div className="hook">“{a.hook}”</div>
            <div className="riskt">
              <b>Risk:</b> {a.risk}
            </div>
          </div>
        ))}
      </div>
    ),
    tests: (
      <div className="tcards">
        {P.tests.map((t, i) => (
          <div className="tcard" key={i}>
            <span className="tid">{t.id}</span>
            <h4>{t.name}</h4>
            <p>{t.hypothesis}</p>
            <div className="arms">
              {t.variants.map((v, j) => (
                <div className="arm" key={j}>
                  <span className="al">{String.fromCharCode(65 + j)}</span>
                  <span className="an">{v.replace(/^[A-D][:.)]\s*/, "")}</span>
                </div>
              ))}
            </div>
            <div className="meta">
              <span className="chip">Decides on: {t.metric}</span>
              <span className="chip">
                ${t.budget_usd} · {t.days} days
              </span>
            </div>
            <div className="pl-need">
              <b>Needs</b>
              <Rich block className="rich pl-p" text={t.min_data} />
            </div>
            <div className="stop">
              <Icon name="shield" />
              <span>Stops early if {t.stop_early_if.replace(/\.$/, "").replace(/^./, (c) => c.toLowerCase())}</span>
            </div>
          </div>
        ))}
      </div>
    ),
    money: (
      <>
        <div className="bsplit">
          {P.budget.split.map((b, i) => (
            <i key={i} style={{ width: `${tot ? (b.usd / tot) * 100 : 0}%`, background: COLOURS[i % COLOURS.length] }} />
          ))}
        </div>
        <div className="legend">
          {P.budget.split.map((b, i) => (
            <div className="lg" key={i}>
              <span className="d" style={{ background: COLOURS[i % COLOURS.length] }} />
              <span>{b.item}</span>
              <b>${fmt(b.usd)}</b>
            </div>
          ))}
        </div>
        <Rich block className="rich pl-p pl-cost" text={P.budget.cost_per_result} />
        <div className="ev3">
          <b>What it expects:</b> {P.budget.expected_results}
        </div>
      </>
    ),
    time: (
      <div className="rows">
        {P.timeline.map((r, i) => (
          <div className="tlr" key={i}>
            <span className="w">{r.when}</span>
            <span>{r.what}</span>
          </div>
        ))}
      </div>
    ),
    run: (
      <>
        <K margin="0 0 8px">What it may do on its own</K>
        <div className="rows">
          {P.autonomy.map((a, i) => (
            <div className="auto2" key={i}>
              <b>{a.action}</b>
              <span className={`mode ${a.mode}`}>{MODE[a.mode]}</span>
              <p>{a.why}</p>
            </div>
          ))}
        </div>
      </>
    ),
    risks: (
      <>
        <div className="rows">
          <div className="rrow h">
            <span>Risk</span>
            <span>Warning sign</span>
            <span>What it will do</span>
          </div>
          {P.risks.map((r, i) => (
            <div className="rrow" key={i}>
              <span>{r.risk}</span>
              <span>{r.signal}</span>
              <span>{r.response}</span>
            </div>
          ))}
        </div>
        {P.change_course_if.length > 0 && (
          <>
            <K>It will change course if</K>
            <ul className="bl">
              {P.change_course_if.map((t, i) => (
                <li key={i}>{t}</li>
              ))}
            </ul>
          </>
        )}
      </>
    ),
    asks: (
      <div className="rows">
        {P.needs_from_you.map((t, i) => (
          <div className="ask-l" key={i}>
            <span className="ck" />
            <span>{t}</span>
          </div>
        ))}
      </div>
    ),
    pbk: (
      <>
        <div className="rows">
          {P.playbook_followed.map((f, i) => (
            <div className="pf" key={i}>
              <span className="use fact">Followed</span>
              <div>
                <b>{f.rule}</b>
                <p>{f.how}</p>
              </div>
            </div>
          ))}
        </div>
        {P.conflicts.length > 0 && (
          <>
            <K colour="var(--warn)">Where it pushes back</K>
            {P.conflicts.map((c, i) => (
              <div className="cfl" key={i}>
                <b>{c.rule}</b>
                <p>
                  <span>Its evidence:</span> {c.evidence}
                </p>
                <p>
                  <span>It suggests:</span> {c.suggestion}
                </p>
              </div>
            ))}
          </>
        )}
      </>
    ),
  };
}

function Comments({ list, open, onClose, onAdd }: { list: PlanComment[]; open: boolean; onClose: () => void; onAdd: (text: string) => Promise<boolean> }) {
  const [text, setText] = useState("");
  return (
    <div>
      {list.map((c) => (
        <div className="cnote" key={c.id}>
          <Icon name="reply" />
          <span>{c.body}</span>
        </div>
      ))}
      {open && (
        <div className="cbox">
          <textarea rows={2} placeholder="What should change here?" value={text} onChange={(e) => setText(e.target.value)} autoFocus aria-label="What should change here?" />
          <button
            type="button"
            className="btn sm"
            onClick={async () => {
              if (!text.trim()) return;
              if (await onAdd(text.trim())) {
                setText("");
                onClose();
              }
            }}
          >
            Add
          </button>
        </div>
      )}
    </div>
  );
}

interface Props {
  plan: Plan;
  version: number;
  approved: boolean;
  comments: PlanComment[];
  small: string;
  onComment: (section: string, body: string) => Promise<boolean>;
  onChanges: () => void;
  onDone: () => void;
}

export function FullPlan({ plan, version, approved, comments, small, onComment, onChanges, onDone }: Props) {
  const [box, setBox] = useState<string | null>(null);
  const B = planBodies(plan);
  return (
    <DocWalk
      parts={PLAN_PARTS.map(([id, title]) => ({ id, title }))}
      head={{ title: "The full plan", ver: `v${version}`, badge: approved ? "Approved" : "Waiting for you", small }}
      body={(p) => (
        <div className="dw-body">
          {B[p.id]}
          {!approved && (
            <div className="dw-tools">
              <button type="button" className="chip link" onClick={() => setBox((b) => (b === p.id ? null : p.id))}>
                <Icon name="reply" />
                Comment
              </button>
            </div>
          )}
          <Comments
            list={comments.filter((c) => c.version === version && c.section === p.title)}
            open={box === p.id}
            onClose={() => setBox(null)}
            onAdd={(text) => onComment(p.title, text)}
          />
        </div>
      )}
      foot={
        approved ? undefined : (
          <button type="button" className="btn sm ghost" onClick={onChanges}>
            Ask for changes
          </button>
        )
      }
      done={{ label: "Back to the review", onClick: onDone }}
    />
  );
}
